import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Layers, Users, CheckCircle2, AlertTriangle, Loader2, RefreshCw, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useAppDialog } from '../../hooks/useAppDialog';

// ──────────────────────────────────────────────────────────────────────────
// SIL Ratio Phase B — Fan-Out Panel
//
// Surfaces the per-shift fan-out state for shared-support (1:2) shifts:
//   - Status pill (Not fanned out / Fanned out vN / Disabled / No permission)
//   - "Run fan-out" button (admin/coord only — gated by permissions)
//   - Allocations list (per participant, with claim hours, rate, amount)
//
// Reads from:  GET  /api/roster/fanout/{shift_id}
// Triggers:    POST /api/roster/fanout/execute
//
// Pure additive UI. When the feature flag `sil_ratio_fanout_enabled` is OFF
// the GET endpoint returns 403 FEATURE_DISABLED and this panel renders a
// "Disabled" pill + slate banner. When the user lacks roster.fanout.view,
// the panel renders a "No permission" pill + slate banner instead.
// ──────────────────────────────────────────────────────────────────────────

const fmtMoney = (n) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(Number(n || 0));

function StatusPill({ data, flagDisabled, fanoutEligible }) {
  if (flagDisabled) {
    return <Badge variant="outline" className="text-slate-600 border-slate-300" data-testid="fanout-status-disabled">Fan-Out Disabled</Badge>;
  }
  if (!fanoutEligible) {
    return <Badge variant="outline" className="text-slate-500 border-slate-200" data-testid="fanout-status-na">Not applicable</Badge>;
  }
  if (data?.shared_support && data?.fanout_version) {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300" data-testid="fanout-status-done">
        Fanned out · v{data.fanout_version}
      </Badge>
    );
  }
  return (
    <Badge className="bg-amber-100 text-amber-800 border-amber-300" data-testid="fanout-status-pending">
      Pending fan-out
    </Badge>
  );
}

export default function CalendarFanoutPanel({ shift, onChanged }) {
  const ratio = shift?.support_ratio;
  const linked = (shift?.linked_participants || []).filter(Boolean);
  const eligible = ratio === '1:2' && linked.length >= 2;

  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [flagDisabled, setFlagDisabled] = useState(false);
  const [permDenied, setPermDenied] = useState(false);
  const dialog = useAppDialog();

  const fetchState = useCallback(async () => {
    if (!eligible || !shift?.id) return;
    setLoading(true);
    try {
      const r = await api.get(`/roster/fanout/${shift.id}`);
      setState(r.data?.data || null);
      setFlagDisabled(false);
      setPermDenied(false);
    } catch (e) {
      const errBody = e?.response?.data;
      const code = errBody?.error?.code
        || errBody?.detail?.error?.code
        || errBody?.detail?.code
        || (typeof errBody?.detail === 'string' ? errBody.detail : null);
      setFlagDisabled(code === 'FEATURE_DISABLED');
      setPermDenied(e?.response?.status === 403 && code !== 'FEATURE_DISABLED');
      setState(null);
    } finally {
      setLoading(false);
    }
  }, [eligible, shift?.id]);

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  const runFanout = useCallback(async () => {
    if (!shift?.id) return;
    const ok = await dialog.confirm({
      title: 'Run fan-out for this shift?',
      description: `This will generate ${linked.length} billing allocations on the existing payroll timesheet. Each participant will be billed for the full shift duration (shared-support standard). Payroll hours stay unchanged.`,
      confirmLabel: 'Run fan-out',
      cancelLabel: 'Cancel',
    });
    if (!ok) return;
    setRunning(true);
    try {
      const r = await api.post(
        `/roster/fanout/execute`,
        { shift_id: shift.id, force: false },
      );
      const d = r.data?.data;
      await dialog.alert({
        title: d?.status === 'ALREADY_FANNED_OUT' ? 'Already fanned out' : 'Fan-out complete',
        description: `${d?.summary?.count || 0} billing allocations · payroll ${d?.payroll_hours || 0}h · total ${fmtMoney(d?.summary?.total_amount)}.`,
      });
      await fetchState();
      onChanged && onChanged();
    } catch (e) {
      const err = e?.response?.data?.error || e?.response?.data?.detail?.error || e?.response?.data?.detail || {};
      await dialog.alert({
        title: 'Fan-out failed',
        description: err.message || err.code || e?.message || 'Unknown error',
        variant: 'danger',
      });
    } finally {
      setRunning(false);
    }
  }, [shift?.id, linked.length, dialog, fetchState, onChanged]);

  if (!eligible) return null;

  const allocations = state?.billing_allocations || [];
  const summary = state?.summary;

  return (
    <div className="rounded-md border border-violet-200 bg-violet-50/40 p-3 space-y-3" data-testid="fanout-panel">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2">
          <Layers className="w-4 h-4 mt-0.5 text-violet-700 shrink-0" />
          <div>
            <div className="text-xs font-semibold text-violet-900 flex items-center gap-2">
              Shared Support Fan-Out
              <StatusPill data={state} flagDisabled={flagDisabled} fanoutEligible={eligible} permDenied={permDenied} />
            </div>
            <div className="text-[11px] text-violet-800/80 mt-0.5">
              1 worker, {linked.length} participants. Each participant is billed for the full duration.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0"
            onClick={fetchState}
            disabled={loading}
            data-testid="fanout-refresh-btn"
            title="Refresh fan-out state"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {flagDisabled && (
        <div className="text-[11px] text-slate-700 bg-slate-100 border border-slate-200 rounded px-2 py-1.5 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-500" />
          <span>
            Fan-Out feature flag is OFF. Phase A guardrail still warns this shift is not billing-ready.
            Enable <code className="bg-white px-1 rounded text-[10px]">sil_ratio_fanout_enabled</code> in
            Feature Flags to use this engine.
          </span>
        </div>
      )}

      {permDenied && (
        <div className="text-[11px] text-slate-700 bg-slate-100 border border-slate-200 rounded px-2 py-1.5 flex items-start gap-1.5" data-testid="fanout-perm-denied-banner">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-500" />
          <span>
            Your role does not have <code className="bg-white px-1 rounded text-[10px]">roster.fanout.view</code>.
            Ask an Admin to grant fan-out permissions, or run <code className="bg-white px-1 rounded text-[10px]">POST /api/admin/seed-roles</code> to sync system role permissions.
          </span>
        </div>
      )}

      {!flagDisabled && state?.shared_support && allocations.length > 0 && (
        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-wide text-violet-900/70 font-semibold">
            Billing allocations · v{state.fanout_version}
          </div>
          <ul className="space-y-1.5">
            {allocations.map((a, idx) => (
              <li
                key={`${a.participant_id}-${idx}`}
                className="rounded border border-violet-200 bg-white px-2.5 py-1.5 text-[11px] flex items-center justify-between"
                data-testid="fanout-allocation-row"
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  <Users className="w-3 h-3 text-violet-600 shrink-0" />
                  <span className="font-mono text-[10px] text-slate-600 truncate">
                    {(a.participant_id || '').slice(-6)}
                  </span>
                  <span className="text-slate-700 truncate">
                    {a.service_name || 'Shared SIL support'}
                  </span>
                  {a.invoice_id && (
                    <Badge variant="outline" className="ml-1 text-[9px] py-0 h-4 px-1 border-emerald-300 text-emerald-700">
                      <FileText className="w-2.5 h-2.5 mr-0.5" /> Invoiced
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0 tabular-nums">
                  <span className="text-slate-500">{Number(a.claim_hours || 0).toFixed(2)}h</span>
                  <span className="text-slate-500">@ {fmtMoney(a.rate)}</span>
                  <span className="font-semibold text-slate-900">{fmtMoney(a.amount)}</span>
                </div>
              </li>
            ))}
          </ul>
          {summary && (
            <div className="text-[10px] text-violet-900/70 flex justify-between border-t border-violet-200 pt-1.5">
              <span>Payroll hours: <span className="font-semibold">{state.payroll_hours}h</span> (single record)</span>
              <span>Total billed: <span className="font-semibold">{fmtMoney(summary.total_amount)}</span></span>
            </div>
          )}
        </div>
      )}

      {!flagDisabled && !state?.shared_support && (
        <div className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-600" />
          <div className="space-y-1">
            <div>This shift has not been fanned out yet.</div>
            {state?.shift_status !== 'COMPLETED' && state?.timesheet_status !== 'APPROVED' && (
              <div className="text-[10px] text-amber-800">
                Fan-out runs only after the shift is COMPLETED or its timesheet is APPROVED.
                {state?.shift_status && <span> (Current shift status: <span className="font-mono">{state.shift_status}</span>)</span>}
              </div>
            )}
          </div>
        </div>
      )}

      {!flagDisabled && (
        <Button
          type="button"
          size="sm"
          onClick={runFanout}
          disabled={running || (state?.shift_status !== 'COMPLETED' && state?.timesheet_status !== 'APPROVED')}
          className="w-full h-8 bg-violet-600 hover:bg-violet-700 text-white"
          data-testid="fanout-run-btn"
        >
          {running ? (
            <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Running…</>
          ) : state?.shared_support ? (
            <><CheckCircle2 className="w-3 h-3 mr-1.5" /> Re-run fan-out (force)</>
          ) : (
            <><Layers className="w-3 h-3 mr-1.5" /> Run fan-out</>
          )}
        </Button>
      )}
    </div>
  );
}
